'use client';

/**
 * AppStoreBadges — branded "Download on the App Store" and "Get it on
 * Google Play" pill buttons, plus simple Apple/Play icon SVGs used in
 * smaller surfaces (smart banner, breadcrumb tags).
 *
 * Design uses both stores' canonical wording ("Download on the / App Store",
 * "GET IT ON / Google Play") with proper typographic weight + the official
 * monochrome glyphs. Buttons are rendered in a uniform black pill with
 * white text, matching the dark slate footer.
 */

import React from 'react';

export const APP_STORE_URL = 'https://apps.apple.com/au/app/camping-australia/id6761671553';
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.campingaustralia.android';

// ─────────── Glyphs (small, used in smart banner + buttons) ───────────

export function ApplePlayIcon({ className = 'h-5 w-5', ...rest }: React.SVGProps<SVGSVGElement>) {
  // viewBox shrunk + path centred so the apple glyph fills its bounding box
  // closer to the way the Google Play triangle does — without this the Apple
  // icon visually sits ~15% smaller than the Play badge alongside it.
  return (
    <svg
      viewBox="0 0 22 22"
      fill="currentColor"
      className={className}
      aria-hidden
      {...rest}
    >
      <path d="M15.365 0.43c0 1.14-.49 2.27-1.27 3.08-.84.86-2.21 1.51-3.42 1.41-.13-1.13.43-2.31 1.18-3.07.84-.85 2.27-1.46 3.51-1.42zM19.5 16.34c-.59 1.37-1.31 2.68-2.43 3.92-.95 1.07-1.91 1.87-3.21 1.84-1.27-.03-1.69-.81-3.13-.81-1.45 0-1.91.78-3.13.84-1.32.05-2.32-1.16-3.27-2.21-1.95-2.18-3.43-6.18-1.44-9.05.99-1.41 2.74-2.31 4.59-2.34 1.27-.03 2.46.85 3.24.85.78 0 2.21-1.05 3.74-.9.64.03 2.43.26 3.58 1.99-.09.06-2.13 1.25-2.11 3.74.03 2.97 2.59 3.96 2.62 3.97z"/>
    </svg>
  );
}

export function GooglePlayIcon({ className = 'h-5 w-5', ...rest }: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
      {...rest}
    >
      <path d="M3.609 1.814 13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734c0-.41.243-.769.609-.92zM14.499 12.71l3.105 3.105-12.69 7.32 9.585-10.425zm0-1.42L4.914.875l12.69 7.32-3.105 3.095zm6.286-1.71-2.95 1.7-3.5 3.5 3.5 3.5 2.95 1.7c.91-.64 1.498-1.685 1.498-2.85V12.06c0-1.165-.589-2.21-1.498-2.85z"/>
    </svg>
  );
}

// ─────────── Full pill badges ───────────
//
// Each badge renders as a fixed-height (40px) flex pill with a uniform
// 24×24 icon slot on the left and a two-line label on the right. The
// icon SVGs are tuned (Apple slightly oversized to match the visual
// weight of the multi-colour Play triangle) so the two badges line up
// cleanly when placed side-by-side.

const BADGE_BASE = 'inline-flex items-center gap-2 rounded-md bg-black text-white no-underline transition hover:bg-zinc-800 active:scale-[0.98]';
const BADGE_HEIGHTS = { sm: 'h-9', md: 'h-10' } as const;
const BADGE_PAD = { sm: 'px-2.5', md: 'px-3' } as const;
const BADGE_ICON = { sm: 'h-5 w-5', md: 'h-[22px] w-[22px]' } as const;
const BADGE_TOP = { sm: 'text-[8px]', md: 'text-[9px]' } as const;
const BADGE_BOT = { sm: 'text-[12px]', md: 'text-[14px]' } as const;

export function AppStoreBadge({
  size = 'md',
  className = '',
  onClick,
}: {
  size?: 'sm' | 'md';
  className?: string;
  onClick?: () => void;
}) {
  return (
    <a
      href={APP_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Download on the App Store"
      className={`${BADGE_BASE} ${BADGE_HEIGHTS[size]} ${BADGE_PAD[size]} ${className}`}
      onClick={onClick}
    >
      <ApplePlayIcon className={`${BADGE_ICON[size]} shrink-0`} />
      <span className="flex flex-col items-start leading-[1.05] text-left">
        <span className={`${BADGE_TOP[size]} tracking-tight text-white/85`}>Download on the</span>
        <span className={`${BADGE_BOT[size]} font-semibold tracking-tight`}>App Store</span>
      </span>
    </a>
  );
}

export function GooglePlayBadge({
  size = 'md',
  className = '',
  onClick,
}: {
  size?: 'sm' | 'md';
  className?: string;
  onClick?: () => void;
}) {
  return (
    <a
      href={PLAY_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Get it on Google Play"
      className={`${BADGE_BASE} ${BADGE_HEIGHTS[size]} ${BADGE_PAD[size]} ${className}`}
      onClick={onClick}
    >
      {/* The official Google Play glyph is multicolour — render gradient triangle */}
      <svg viewBox="0 0 24 24" className={`${BADGE_ICON[size]} shrink-0`} aria-hidden>
        <defs>
          <linearGradient id="gp-blue" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#00C3FF" />
            <stop offset="100%" stopColor="#1A73E8" />
          </linearGradient>
          <linearGradient id="gp-green" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#00F076" />
            <stop offset="100%" stopColor="#00C76A" />
          </linearGradient>
          <linearGradient id="gp-yellow" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#FFD600" />
            <stop offset="100%" stopColor="#FFBA00" />
          </linearGradient>
          <linearGradient id="gp-red" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FF3A44" />
            <stop offset="100%" stopColor="#C31162" />
          </linearGradient>
        </defs>
        <path fill="url(#gp-blue)" d="M3.609 1.814 13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734c0-.41.243-.769.609-.92z"/>
        <path fill="url(#gp-green)" d="M14.499 11.29 4.914.875l12.69 7.32-3.105 3.095z"/>
        <path fill="url(#gp-yellow)" d="m17.604 15.815-3.105-3.105 3.105-3.105 3.181 1.835c.91.64 1.498 1.685 1.498 2.85 0 1.165-.589 2.21-1.498 2.85l-3.181 1.835v-3.16z" transform="translate(0 -1.7)"/>
        <path fill="url(#gp-red)" d="M14.499 12.71 4.914 23.125l12.69-7.32-3.105-3.095z"/>
      </svg>
      <span className="flex flex-col items-start leading-[1.05] text-left">
        <span className={`${BADGE_TOP[size]} tracking-tight text-white/85`}>GET IT ON</span>
        <span className={`${BADGE_BOT[size]} font-semibold tracking-tight`}>Google Play</span>
      </span>
    </a>
  );
}

export function AppStoreBadges({
  size = 'md',
  className = '',
  onNavigate,
}: {
  size?: 'sm' | 'md';
  className?: string;
  /** Fired when a badge is clicked. Reporting only — navigation proceeds. */
  onNavigate?: (destination: 'app-store' | 'play-store') => void;
}) {
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      <AppStoreBadge size={size} onClick={() => onNavigate?.('app-store')} />
      <GooglePlayBadge size={size} onClick={() => onNavigate?.('play-store')} />
    </div>
  );
}
